import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CODE_MANAGEMENT_NOTICE,
  CREATOR_STATUS_COLORS,
  EMPTY_STATES,
  creatorDetailRoute,
  getStatusColor,
} from '../constants';
import { formatDate } from '../utils/formatters';
import { InlineNotice, TableActions, TopControlBar } from '../Styles';
import CreatorProgramTable from '../components/CreatorProgramTable';
import ListPagination from '../components/ListPagination';
import QueryErrorState from '../components/QueryErrorState';
import { usePaginationState } from '../hooks/usePaginationState';
import { useCreators } from '../hooks/useCreatorQueries';
import CreatorCreateDrawer from '../CreatorCreateDrawer';
import CreatorEditDrawer from '../CreatorEditDrawer';
import type { Creator } from '../types';

const { Text } = Typography;

/**
 * §1.1 — creators list.
 *
 * A row's `terms` array is the full version history rather than "current terms", and
 * the docs are explicit that resolving the active allowance client-side is the wrong
 * move — so this table shows no allowance column. That lives on the creator detail
 * screen, which gets it resolved server-side.
 */
const CreatorsTab: React.FC = () => {
  const navigate = useNavigate();

  const { page, pageSize, onPaginationChange } = usePaginationState();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCreator, setEditingCreator] = useState<Creator | null>(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useCreators(page, pageSize);

  const creators = data?.data ?? [];
  const total = data?.total ?? 0;

  const goToCreator = useCallback(
    (creatorId: string) => navigate(creatorDetailRoute(creatorId)),
    [navigate]
  );

  const columns: ColumnsType<Creator> = useMemo(
    () => [
      {
        title: 'CREATOR',
        key: 'name',
        fixed: 'left',
        width: 220,
        render: (_, record) => (
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.handle}
            </Text>
          </div>
        ),
      },
      {
        title: 'EMAIL',
        dataIndex: 'email',
        key: 'email',
        width: 240,
      },
      {
        title: 'CODE PREFIX',
        key: 'codePrefix',
        width: 140,
        render: (_, record) => <Tag>{record.codePrefix}</Tag>,
      },
      {
        title: 'STATUS',
        key: 'status',
        width: 120,
        render: (_, record) => (
          <Tag color={getStatusColor(record.status, CREATOR_STATUS_COLORS)}>
            {record.status}
          </Tag>
        ),
      },
      {
        title: 'CODES',
        key: 'codes',
        width: 90,
        render: (_, record) => record.codes?.length ?? '—',
      },
      {
        title: 'CREATED',
        key: 'createdAt',
        width: 140,
        render: (_, record) => formatDate(record.createdAt),
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        width: 110,
        render: (_, record) => (
          <TableActions>
            <Tooltip title="Edit status or code prefix">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={(event) => {
                  // The row itself navigates to the detail screen.
                  event.stopPropagation();
                  setEditingCreator(record);
                }}
              />
            </Tooltip>
          </TableActions>
        ),
      },
    ],
    []
  );

  if (isError && creators.length === 0) {
    return (
      <QueryErrorState
        title="Could not load creators"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <TopControlBar>
        <div />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsCreateOpen(true)}
        >
          Add Creator
        </Button>
      </TopControlBar>

      <InlineNotice>{CODE_MANAGEMENT_NOTICE}</InlineNotice>

      <CreatorProgramTable<Creator>
        columns={columns}
        dataSource={creators}
        rowKey="id"
        loading={isLoading || isFetching}
        // The shared Table renders `emptyDescription || emptyText`, so passing both
        // silently drops the headline — send one node carrying both lines instead.
        emptyDescription={
          <>
            <div>{EMPTY_STATES.CREATORS}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {EMPTY_STATES.CREATORS_DESCRIPTION}
            </div>
          </>
        }
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: () => goToCreator(record.id),
        })}
      />

      <ListPagination
        visible={creators.length > 0}
        totalCount={total}
        currentPage={page}
        pageSize={pageSize}
        onChange={onPaginationChange}
        totalLabel="Total creators"
      />

      <CreatorCreateDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      <CreatorEditDrawer
        open={!!editingCreator}
        creator={editingCreator}
        onClose={() => setEditingCreator(null)}
      />
    </>
  );
};

export default CreatorsTab;
